import { useEffect } from 'react';
import { type RouteContext } from 'react-resource-router';

/* eslint-disable no-restricted-imports */
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';
import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';
/* eslint-enable no-restricted-imports */
import { useOneTrustContext } from '@app/onetrust';
import { Sentry } from '@app/sentry';

/**
 * Central consent-aware UFO watcher hook. Call ONCE from App.tsx (not from page
 * components). Gated behind OneTrust Performance Cookies consent.
 *
 * The page-load vs. transition decision is driven by UFO's live state via
 * getActiveInteraction(), NOT routeContext.action. On Community the route
 * resolves twice on initial load and (in prod) the corrective resolution
 * arrives as REPLACE/PUSH, so keying off action fired traceUFOTransition and
 * ABORTED the page-load. Using the active interaction is immune to that:
 *   - No active interaction -> traceUFOPageLoad   (genuine initial load)
 *   - Otherwise             -> traceUFOTransition (real SPA navigation)
 *
 * TEMPORARY: Sentry breadcrumbs verify behavior in prod; remove once
 * confirmed. Page components use <UFOSegment>/<UFOLoadHold>.
 *
 * @param routeContext - The router state from useRouter() (inside a <Router>)
 */
export const useReactUFOWatcher = (routeContext: RouteContext): void => {
  const { analyticsOptOut } = useOneTrustContext();

  useEffect(() => {
    if (analyticsOptOut || !routeContext.route) {
      return;
    }

    const experienceName = routeContext.route.name.toLowerCase();

    // Decide from UFO's live state, not routeContext.action.
    const activeInteraction = getActiveInteraction();
    let decision: 'page_load' | 'transition';
    if (!activeInteraction) {
      decision = 'page_load';
      traceUFOPageLoad(experienceName);
    } else {
      decision = 'transition';
      traceUFOTransition(experienceName);
    }

    // TEMPORARY DEBUG: breadcrumb what we reported vs. the actual navigation
    // target so we can confirm behaviour in prod. Remove once verified.
    // eslint-disable-next-line no-restricted-syntax
    Sentry.addBreadcrumb({
      category: 'ufo-watcher',
      level: 'info',
      message: `ufo-watcher: ${decision} -> ${experienceName}`,
      data: {
        experienceName,
        decision,
        locationPathname: routeContext.location?.pathname ?? null,
        matchedRoutePath: routeContext.route?.path ?? null,
        routeName: routeContext.route?.name ?? null,
        routerAction: routeContext.action,
        activeInteractionType: activeInteraction?.type ?? null,
        activeInteractionName: activeInteraction?.ufoName ?? null,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeContext.route, routeContext.action, analyticsOptOut]);
};
